import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { isMatch, omit } from "lodash-es";
import { api } from "./api";
import { useInterval } from "./hooks";

// Only repeated 404s indicate expiration; transient request failures keep polling.
const MAX_CONSECUTIVE_MISSES = 3;

const IDLE_STATUS = { status: null, active: false, pctComplete: 0, message: null, error: null, payload: null };

/**
 * Polls a StatusTracked job immediately and then at intervalMs until it becomes terminal.
 * Calls onFinished for completion and onFailed for other terminal or expired statuses.
 */
export const useJobStatus = (jobId, { intervalMs = 2000, onFinished, onFailed } = {}) => {
  const [jobStatus, setJobStatus] = useState(IDLE_STATUS);
  const missesRef = useRef(0);
  const settledRef = useRef(false);
  const currentJobIdRef = useRef(jobId);
  const inFlightRef = useRef(false);
  // Commit callback changes before an in-flight response can resume.
  const callbacksRef = useRef({ onFinished, onFailed });
  useLayoutEffect(() => {
    callbacksRef.current = { onFinished, onFailed };
  }, [onFinished, onFailed]);

  // Commit the new jid before asynchronous responses resume, then reset its polling state.
  useLayoutEffect(() => {
    currentJobIdRef.current = jobId;
    missesRef.current = 0;
    settledRef.current = false;
    inFlightRef.current = false;
    setJobStatus(IDLE_STATUS);
  }, [jobId]);

  const settle = (finalStatus) => {
    if (settledRef.current) return;

    settledRef.current = true;
    if (finalStatus.status === "complete") {
      callbacksRef.current.onFinished?.(finalStatus);
    } else {
      callbacksRef.current.onFailed?.(finalStatus);
    }
  };

  const poll = async () => {
    // Interval callbacks may lag a render, so always read the committed jid.
    const polledJobId = currentJobIdRef.current;
    if (!polledJobId || inFlightRef.current) return;

    inFlightRef.current = true;
    try {
      const { data } = await api.get(`/job_statuses/${polledJobId}`);
      if (polledJobId !== currentJobIdRef.current) return;

      missesRef.current = 0;
      const next = {
        status: data.status,
        active: Boolean(data.active),
        pctComplete: data.pct_complete ?? 0,
        message: data.message ?? null,
        error: data.error ?? null,
        payload: data.payload ?? null,
      };
      // Skip the re-render when an active poll reports no change.
      setJobStatus((prev) =>
        isMatch(prev, { status: next.status, pctComplete: next.pctComplete, message: next.message, error: next.error }) ? prev : next
      );
      if (!next.active) settle(next);
    } catch (err) {
      if (polledJobId !== currentJobIdRef.current) return;
      if (err.response?.status !== 404) return;

      missesRef.current += 1;
      if (missesRef.current >= MAX_CONSECUTIVE_MISSES) {
        const next = { ...IDLE_STATUS, status: "expired" };
        setJobStatus(next);
        settle(next);
      }
    } finally {
      if (polledJobId === currentJobIdRef.current) inFlightRef.current = false;
    }
  };

  // Poll immediately on a new job; useInterval waits a full interval before its first tick.
  useEffect(() => {
    if (jobId) poll();
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = Boolean(jobId) && (jobStatus.status === null || jobStatus.active);

  useInterval(poll, isActive ? intervalMs : null);

  return { ...omit(jobStatus, "active"), isActive };
};
